/**
 * Página de Configurações SIMPLIFICADA - JurisPrazo V2
 * Interface simples para advogados - apenas o essencial
 */

import React, { useState, useEffect } from 'react';
import {
  Settings, User, Bell, ArrowLeft, Save, 
  Plus, Trash2, Edit3, Shield
} from 'lucide-react';

// Serviços
import { FirebaseService } from '../services/firebaseService';
import { SyncService } from '../services/syncService';

// Componentes
import SyncModal from '../components/SyncModal';

// Lista completa de UFs brasileiras
const UFS_BRASILEIRAS = [
  { value: 'AC', label: 'AC - Acre' },
  { value: 'AL', label: 'AL - Alagoas' },
  { value: 'AP', label: 'AP - Amapá' },
  { value: 'AM', label: 'AM - Amazonas' },
  { value: 'BA', label: 'BA - Bahia' },
  { value: 'CE', label: 'CE - Ceará' },
  { value: 'DF', label: 'DF - Distrito Federal' },
  { value: 'ES', label: 'ES - Espírito Santo' },
  { value: 'GO', label: 'GO - Goiás' },
  { value: 'MA', label: 'MA - Maranhão' },
  { value: 'MT', label: 'MT - Mato Grosso' },
  { value: 'MS', label: 'MS - Mato Grosso do Sul' },
  { value: 'MG', label: 'MG - Minas Gerais' },
  { value: 'PA', label: 'PA - Pará' },
  { value: 'PB', label: 'PB - Paraíba' },
  { value: 'PR', label: 'PR - Paraná' },
  { value: 'PE', label: 'PE - Pernambuco' },
  { value: 'PI', label: 'PI - Piauí' },
  { value: 'RJ', label: 'RJ - Rio de Janeiro' },
  { value: 'RN', label: 'RN - Rio Grande do Norte' },
  { value: 'RS', label: 'RS - Rio Grande do Sul' },
  { value: 'RO', label: 'RO - Rondônia' },
  { value: 'RR', label: 'RR - Roraima' },
  { value: 'SC', label: 'SC - Santa Catarina' },
  { value: 'SP', label: 'SP - São Paulo' },
  { value: 'SE', label: 'SE - Sergipe' },
  { value: 'TO', label: 'TO - Tocantins' }
];

const ConfiguracoesPage = ({ currentUser, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dados');
  
  // Estados do perfil
  const [perfil, setPerfil] = useState({
    displayName: '',
    email: '',
    telefone: '',
    oab: '',
    escritorio: ''
  });

  // Estados das OABs
  const [oabs, setOabs] = useState([]);
  const [novaOab, setNovaOab] = useState({ numero: '', uf: '' });
  const [editandoOab, setEditandoOab] = useState(null);

  // Estados dos alertas (simplificado)
  const [alertas, setAlertas] = useState({
    email: true,
    buscaAutomatica: true,
    horarioBusca: 'ambos' // 'manha', 'tarde', 'ambos'
  });

  const [showSyncModal, setShowSyncModal] = useState(false);

  useEffect(() => {
    if (currentUser?.uid && !currentUser.isDemoUserManual) {
      loadConfiguracoes();
    } else {
      loadDemoConfiguracoes();
    }
  }, [currentUser]);

  const loadConfiguracoes = async () => {
    try {
      setLoading(true);
      
      // Carregar perfil
      const userProfile = await FirebaseService.getUserProfile(currentUser.uid);
      setPerfil({
        displayName: userProfile.displayName || currentUser.displayName || '',
        email: userProfile.email || currentUser.email || '',
        telefone: userProfile.telefone || '',
        oab: userProfile.oab || '',
        escritorio: userProfile.escritorio || ''
      });

      // Carregar OABs
      const userOabs = await FirebaseService.getUserOABs(currentUser.uid);
      setOabs(userOabs);

      // Carregar configurações de sincronização
      const syncStatus = await SyncService.getSyncStatus(currentUser.uid);

      // Carregar configurações de alertas (simplificado)
      const notifConfig = await FirebaseService.getUserNotificationSettings(currentUser.uid);
      setAlertas({
        email: notifConfig.email || true,
        buscaAutomatica: syncStatus.autoSyncEnabled || true,
        horarioBusca: syncStatus.syncSchedule?.length > 1 ? 'ambos' : 'manha'
      });

    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDemoConfiguracoes = () => {
    setPerfil({
      displayName: 'Usuário Demo',
      email: 'demo@jurisprazo.com',
      telefone: '(69) 99999-9999',
      oab: 'RO12345',
      escritorio: 'Escritório Demo Advocacia'
    });

    setOabs([
      { id: 'demo1', numero: '12345', uf: 'RO' },
      { id: 'demo2', numero: '67890', uf: 'SP' }
    ]);

    setLoading(false);
  };

  const handleSalvarPerfil = async () => {
    try {
      setSaving(true);
      
      if (!currentUser.isDemoUserManual) {
        await FirebaseService.saveUserProfile(currentUser.uid, perfil);
        alert('✅ Perfil salvo com sucesso!');
      } else {
        alert('✅ Perfil salvo! (Modo demonstração)');
      }
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      alert('❌ Erro ao salvar perfil: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAdicionarOab = async () => {
    if (!novaOab.numero || !novaOab.uf) {
      alert('⚠️ Preencha o número da OAB e selecione a UF');
      return;
    }

    try {
      setSaving(true);
      
      if (!currentUser.isDemoUserManual) {
        const oabData = {
          numero: novaOab.numero,
          uf: novaOab.uf,
          userId: currentUser.uid
        };
        
        await FirebaseService.addUserOAB(currentUser.uid, oabData);
        
        // Recarregar OABs
        const userOabs = await FirebaseService.getUserOABs(currentUser.uid);
        setOabs(userOabs);
        
        alert('✅ OAB adicionada com sucesso!');
      } else {
        // Modo demo
        const newOab = {
          id: `demo${Date.now()}`,
          numero: novaOab.numero,
          uf: novaOab.uf
        };
        setOabs(prev => [...prev, newOab]);
        alert('✅ OAB adicionada! (Modo demonstração)');
      }
      
      setNovaOab({ numero: '', uf: '' });
    } catch (error) {
      console.error('Erro ao adicionar OAB:', error);
      alert('❌ Erro ao adicionar OAB: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoverOab = async (oabId) => {
    if (!confirm('Tem certeza que deseja remover esta OAB?')) return;

    try {
      setSaving(true);
      
      if (!currentUser.isDemoUserManual) {
        await FirebaseService.removeUserOAB(currentUser.uid, oabId);
        const userOabs = await FirebaseService.getUserOABs(currentUser.uid);
        setOabs(userOabs);
        alert('✅ OAB removida com sucesso!');
      } else {
        setOabs(prev => prev.filter(oab => oab.id !== oabId));
        alert('✅ OAB removida! (Modo demonstração)');
      }
    } catch (error) {
      console.error('Erro ao remover OAB:', error);
      alert('❌ Erro ao remover OAB: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSalvarAlertas = async () => {
    try {
      setSaving(true);
      
      if (!currentUser.isDemoUserManual) {
        // Salvar configurações de notificação
        await FirebaseService.saveUserNotificationSettings(currentUser.uid, {
          email: alertas.email
        });
        
        // Salvar configurações de sincronização baseadas no horário escolhido
        let horarios = [];
        if (alertas.buscaAutomatica) {
          if (alertas.horarioBusca === 'manha') {
            horarios = ['09:00'];
          } else if (alertas.horarioBusca === 'tarde') {
            horarios = ['14:00'];
          } else if (alertas.horarioBusca === 'ambos') {
            horarios = ['09:00', '14:00'];
          }
        }
        
        await SyncService.updateSyncSchedule(currentUser.uid, horarios);
        
        alert('✅ Alertas salvos com sucesso!');
      } else {
        alert('✅ Alertas salvos! (Modo demonstração)');
      }
    } catch (error) {
      console.error('Erro ao salvar alertas:', error);
      alert('❌ Erro ao salvar alertas: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncComplete = async (result) => {
    // Atualizar status de sync após sincronização manual
    if (!currentUser.isDemoUserManual) {
      const syncStatus = await SyncService.getSyncStatus(currentUser.uid);
    }
  };

  const tabs = [
    { id: 'dados', label: 'Dados Básicos', icon: User },
    { id: 'alertas', label: 'Alertas', icon: Bell }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header da Página */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">⚙️ Configurações</h1>
        <p className="text-slate-600 mt-1">Gerencie seu perfil, OABs e alertas</p>
      </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Menu Lateral */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <nav className="space-y-2">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg transition-all font-medium
                        ${activeTab === tab.id
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'text-slate-700 hover:bg-slate-50'
                        }
                      `}
                    >
                      <Icon size={20} />
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Conteúdo Principal */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              {/* Header da Seção */}
              <div className="bg-slate-800 px-6 py-4">
                <h2 className="text-xl font-bold text-white">
                  {tabs.find(t => t.id === activeTab)?.label}
                </h2>
              </div>

              {/* Conteúdo da Seção */}
              <div className="p-6">
                {/* Aba Dados Básicos */}
                {activeTab === 'dados' && (
                  <div className="space-y-6">
                    {/* Dados Pessoais */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nome Completo
                        </label>
                        <input
                          type="text"
                          value={perfil.displayName}
                          onChange={(e) => setPerfil(prev => ({ ...prev, displayName: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Seu nome completo"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          E-mail
                        </label>
                        <input
                          type="email"
                          value={perfil.email}
                          onChange={(e) => setPerfil(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="seu@email.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Telefone
                        </label>
                        <input
                          type="tel"
                          value={perfil.telefone}
                          onChange={(e) => setPerfil(prev => ({ ...prev, telefone: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="(00) 00000-0000"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nome do Escritório
                        </label>
                        <input
                          type="text"
                          value={perfil.escritorio}
                          onChange={(e) => setPerfil(prev => ({ ...prev, escritorio: e.target.value }))}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Nome do seu escritório"
                        />
                      </div>
                    </div>

                    {/* Botão Salvar Perfil */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleSalvarPerfil}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold rounded-lg transition-colors"
                      >
                        <Save size={16} />
                        {saving ? 'Salvando...' : 'Salvar Perfil'}
                      </button>
                    </div>
                    
                    {/* Seção OABs */}
                    <div className="border-t border-gray-200 pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Shield size={20} className="text-blue-600" />
                        Suas OABs
                      </h3>
                      
                      {/* Adicionar Nova OAB */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
                        <h4 className="text-lg font-bold text-blue-900 mb-4">Adicionar Nova OAB</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-2">
                              Número da OAB
                            </label>
                            <input
                              type="text"
                              value={novaOab.numero}
                              onChange={(e) => setNovaOab(prev => ({ ...prev, numero: e.target.value }))}
                              className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Ex: 123456"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-700 mb-2">
                              Estado (UF)
                            </label>
                            <select
                              value={novaOab.uf}
                              onChange={(e) => setNovaOab(prev => ({ ...prev, uf: e.target.value }))}
                              className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Selecione a UF</option>
                              {UFS_BRASILEIRAS.map(uf => (
                                <option key={uf.value} value={uf.value}>{uf.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex items-end">
                            <button
                              onClick={handleAdicionarOab}
                              disabled={saving}
                              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold rounded-lg transition-colors"
                            >
                              <Plus size={16} />
                              {saving ? 'Adicionando...' : 'Adicionar'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Lista de OABs */}
                      <div className="space-y-3">
                        {oabs.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">
                            <Shield size={48} className="mx-auto mb-3 text-gray-300" />
                            <p>Nenhuma OAB cadastrada ainda</p>
                            <p className="text-sm">Adicione suas OABs para sincronizar os dados</p>
                          </div>
                        ) : (
                          oabs.map(oab => (
                            <div key={oab.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                              <div className="flex items-center gap-3">
                                <Shield size={20} className="text-blue-600" />
                                <div>
                                  <p className="font-semibold text-gray-900">OAB {oab.uf} {oab.numero}</p>
                                  <p className="text-sm text-gray-600">{UFS_BRASILEIRAS.find(uf => uf.value === oab.uf)?.label || oab.uf}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoverOab(oab.id)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                              >
                                <Trash2 size={14} />
                                Remover
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Aba Alertas */}
                {activeTab === 'alertas' && (
                  <div className="space-y-6">
                    {/* Alertas por Email */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-blue-900 mb-2">📧 Alertas por Email</h3>
                          <p className="text-sm text-blue-700">Receber notificações de prazos no seu email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={alertas.email}
                            onChange={(e) => setAlertas(prev => ({ ...prev, email: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    </div>

                    {/* Busca Automática */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-green-900 mb-2">🔍 Buscar Prazos Automaticamente</h3>
                          <p className="text-sm text-green-700">Sistema busca novos prazos automaticamente</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={alertas.buscaAutomatica}
                            onChange={(e) => setAlertas(prev => ({ ...prev, buscaAutomatica: e.target.checked }))}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                      </div>

                      {/* Horário da Busca */}
                      {alertas.buscaAutomatica && (
                        <div className="border-t border-green-200 pt-4">
                          <label className="block text-sm font-medium text-green-900 mb-3">⏰ Quando buscar:</label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <label className="flex items-center p-3 border border-green-300 rounded-lg cursor-pointer hover:bg-green-100">
                              <input
                                type="radio"
                                name="horarioBusca"
                                value="manha"
                                checked={alertas.horarioBusca === 'manha'}
                                onChange={(e) => setAlertas(prev => ({ ...prev, horarioBusca: e.target.value }))}
                                className="mr-3 text-green-600 focus:ring-green-500"
                              />
                              <div>
                                <div className="font-medium text-green-900">🌅 Manhã (9h)</div>
                                <div className="text-xs text-green-700">Uma busca por dia</div>
                              </div>
                            </label>
                            
                            <label className="flex items-center p-3 border border-green-300 rounded-lg cursor-pointer hover:bg-green-100">
                              <input
                                type="radio"
                                name="horarioBusca"
                                value="tarde"
                                checked={alertas.horarioBusca === 'tarde'}
                                onChange={(e) => setAlertas(prev => ({ ...prev, horarioBusca: e.target.value }))}
                                className="mr-3 text-green-600 focus:ring-green-500"
                              />
                              <div>
                                <div className="font-medium text-green-900">🌇 Tarde (14h)</div>
                                <div className="text-xs text-green-700">Uma busca por dia</div>
                              </div>
                            </label>
                            
                            <label className="flex items-center p-3 border border-green-300 rounded-lg cursor-pointer hover:bg-green-100">
                              <input
                                type="radio"
                                name="horarioBusca"
                                value="ambos"
                                checked={alertas.horarioBusca === 'ambos'}
                                onChange={(e) => setAlertas(prev => ({ ...prev, horarioBusca: e.target.value }))}
                                className="mr-3 text-green-600 focus:ring-green-500"
                              />
                              <div>
                                <div className="font-medium text-green-900">🔄 Ambos</div>
                                <div className="text-xs text-green-700">Manhã e tarde</div>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Botão Salvar */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleSalvarAlertas}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold rounded-lg transition-colors"
                      >
                        <Save size={16} />
                        {saving ? 'Salvando...' : 'Salvar Alertas'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      
      {/* Modal de Sincronização Manual */}
      <SyncModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        currentUser={currentUser}
        onSyncComplete={handleSyncComplete}
      />
    </div>
  );
};

export default ConfiguracoesPage;
